import React from 'react';
import { classNames } from '@/lib/utils';
import { useOffline } from '@/hooks/useOffline';
import { useSync } from '@/hooks/useSync';

interface OfflineIndicatorProps {
    className?: string;
}

export function OfflineIndicator({ className }: OfflineIndicatorProps) {
    const { isOnline, wasOffline } = useOffline();
    const { pendingCount, isSyncing } = useSync();

    // Online sem pendências - não mostrar nada
    if (isOnline && pendingCount === 0 && !wasOffline) {
        return null;
    }

    return (
        <div
            className={classNames(
                'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
                className
            )}
        >
            {/* Offline indicator */}
            {!isOnline && (
                <div className="bg-status-warning text-content-title px-4 py-2 flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a5 5 0 01-7.072 0m7.072 0l-2.829-2.829m-2.829 2.829L3 21" />
                    </svg>
                    <span className="text-sm font-medium">
                        Você está offline
                        {pendingCount > 0 && ` • ${pendingCount} ${pendingCount === 1 ? 'item pendente' : 'itens pendentes'}`}
                    </span>
                </div>
            )}

            {/* Syncing indicator */}
            {isOnline && isSyncing && (
                <div className="bg-brand-surface text-brand-primary px-4 py-2 flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm font-medium">
                        Sincronizando...
                    </span>
                </div>
            )}

            {/* Back online indicator */}
            {wasOffline && isOnline && !isSyncing && (
                <div className="bg-status-success/20 text-status-success px-4 py-2 flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">
                        Conexão restaurada
                    </span>
                </div>
            )}

            {/* Pending items while online */}
            {isOnline && pendingCount > 0 && !isSyncing && !wasOffline && (
                <div className="bg-brand-surface text-brand-primary px-4 py-2 flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="text-sm font-medium">
                        {pendingCount} {pendingCount === 1 ? 'item' : 'itens'} aguardando sincronização
                    </span>
                </div>
            )}
        </div>
    );
}

export default OfflineIndicator;
